import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthHeader from './components/AuthHeader';
import LoginForm from './components/LoginForm';
import SecurityBadges from './components/SecurityBadges';

// Scoped background style for this page only
const loginPageBgStyle = `
.login-auth-page {
  background: linear-gradient(135deg, #233c2e 0%, #2e2540 100%);
  min-height: 100vh;
}
`;

const LoginAuthentication = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleLogin = async (credentials) => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: credentials.name.trim(),
          className: String(credentials.role).trim().toLowerCase() === 'admin'
            ? ''
            : credentials.className.trim(),
          role: String(credentials.role).trim().toLowerCase(),
          token: credentials.token,
          term: String(credentials.term).trim().toLowerCase()
        })
      });

      const result = await response.json();

      if (!response.ok) {
        setErrorMsg(result.error || 'Login failed. Please try again.');
        setIsLoading(false);
        return;
      }

      if (!result.user || !result.user._id) {
        setErrorMsg('Login failed: Missing user ID.');
        setIsLoading(false);
        return;
      }

      localStorage.setItem('user', JSON.stringify({
        name: result.user.name,
        role: result.user.role,
        className: result.user.className,
        term: result.user.term,
        _id: result.user._id,
        isAuthenticated: true,
        loginTime: new Date().toISOString()
      }));

      localStorage.setItem('userName', result.user.name);

      if (result.user.role === 'teacher') {
        localStorage.setItem('teacherData', JSON.stringify({
          ...result.user,
          _id: result.user._id,
          activeClass: result.user.activeClass // Store the selected/assigned class
        }));
        window.location.replace('/teacher-portal');
        setIsLoading(false);
        return;
      }

      if (result.user.role === 'student') {
        localStorage.setItem('studentData', JSON.stringify({
          ...result.user,
          _id: result.user._id,
          gradeLevel: result.user.gradeLevel || result.user.className || "",
          personal: result.user.personal || {},
          academic: result.user.academic || {},
          className: result.user.className || "",
          term: result.user.term || ""
        }));
        window.location.replace('/student-dashboard');
        setIsLoading(false);
        return;
      }

      if (result.user.role === 'admin') {
        localStorage.setItem('adminData', JSON.stringify({
          ...result.user,
          _id: result.user._id
        }));
        window.location.replace('/admin');
        setIsLoading(false);
        return;
      }

      navigate('/');
    } catch (error) {
      setErrorMsg('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{loginPageBgStyle}</style>
      <div className="login-auth-page min-h-screen bg-background">
        <div className="relative z-10 min-h-screen flex">
          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
            <div className="w-full max-w-md">
              <div className="bg-card border border-border rounded-xl shadow-elevation p-6 lg:p-8">
                <AuthHeader />
                <div className="space-y-6">
                  {errorMsg && (
                    <div className="bg-error/10 border border-error text-error rounded-md px-4 py-2 mb-2 text-sm">
                      {errorMsg}
                    </div>
                  )}
                  <LoginForm 
                    onLogin={handleLogin}
                    isLoading={isLoading}
                  />
                  <div className="mt-4 flex flex-col items-center justify-center text-base font-semibold text-muted-foreground">
                    <span>
                      If you are having any issue with your login or dashboard, please reach out to our support team.
                    </span>
                    <button
                      className="mt-2 text-primary underline hover:text-primary/80 transition"
                      onClick={() => navigate('/contact')}
                      type="button"
                    >
                      Contact support
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Security Information Sidebar - Desktop Only */}
          <div className="hidden lg:flex lg:w-96 lg:items-center lg:justify-center lg:p-8">
            <div className="w-full max-w-sm">
              <SecurityBadges />
            </div>
          </div>
        </div>
        {/* Mobile Security Info */}
        <div className="lg:hidden bg-muted/30 border-t border-border p-4">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span>Secure Platform</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>Data Protected</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span>SSL Encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginAuthentication;